import { isBooleanFalse, isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { toName } from "../utils/stringFormatter";
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";
import CONSTANTS from "../constants";
import { getClientIPAddress } from "../utils/clientUtils";

class Agreement {
    static collectionName = 'agreements';

    key = null; // identifier used to embed
    name = null; // display name
    content = null; // content of the agreement
    effectiveDate = null; // displayed to the user
    updatedDate = null; // displayed to the user
    active = false;
    coveredEntityId = null; // if the agreement is specific to a covered entity

    constructor(agreement) {
        if (isObjectNotEmpty(agreement)) {
            if (isStringNotEmpty(agreement.key)) { this.key = agreement.key; }
            if (isStringNotEmpty(agreement.name)) { this.name = agreement.name; }
            if (isStringNotEmpty(agreement.content)) { this.content = agreement.content; }
            if (isNotNullOrUndefined(agreement.effectiveDate)) { this.effectiveDate = agreement.effectiveDate; }
            if (isNotNullOrUndefined(agreement.updatedDate)) { this.updatedDate = agreement.updatedDate; }
            if (isBooleanTrue(agreement.active)) { this.active = true; }
            if (isStringNotEmpty(agreement.coveredEntityId)) { this.coveredEntityId = agreement.coveredEntityId; }
        }
    }

    toDto = () => {
        return {
            key: this.key,
            name: this.name,
            content: this.content,
            effectiveDate: this.effectiveDate,
            updatedDate: this.updatedDate,
            active: this.active,
            coveredEntityId: this.coveredEntityId
        };
    }

    isValid = () => {
        if (isStringEmpty(this.key)) {
            throw new Error('Key is required.');
        }
        if (isStringEmpty(this.name)) {
            throw new Error('Name is required.');
        }
        if (isStringEmpty(this.content)) {
            throw new Error('Content is required.');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            return await create(Agreement.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Agreement.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedAgreement) => {
        const updatedObj = new Agreement(updatedAgreement);
        if (updatedObj.isValid() && id === updatedAgreement.id) {
            return await update(Agreement.collectionName, id, updatedObj.toDto());
        } else {
            throw new Error('id and id field of the updated agreement must match.')
        }
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Agreement.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Agreement.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Agreement.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Agreement.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Agreement.collectionName, ids, 'in');
    }

    static getByKeyAndActive = async (key) => {
        let queryConstraints = [];

        queryConstraints.push(where('key', '==', key));
        queryConstraints.push(where('active', '==', true));

        let q = query(collection(db, Agreement.collectionName), ...queryConstraints);

        let snapShot = await getDocs(q);
        let results = snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });

        if (isListNotEmpty(results)) {
            return results[0];
        }

        return null;
    }

    static getCount = async (queryParameters) => {
        const { key, active, coveredEntityId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(key)) {
            queryConstraints.push(where('key', 'in', key));
        } else if (isStringNotEmptyOrZeroLength(key)) {
            queryConstraints.push(where('key', '==', key));
        }

        if (isBooleanTrue(active)) {
            queryConstraints.push(where('active', '==', true));
        } else if (isBooleanFalse(active)) {
            queryConstraints.push(where('active', '==', false));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', [...coveredEntityId, 'ALL']));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', [coveredEntityId, 'ALL']));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Agreement.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { key, active, coveredEntityId, start, end } = queryParameters;

        let queryConstraints = [];

        if (isListNotEmpty(key)) {
            queryConstraints.push(where('key', 'in', key));
        } else if (isStringNotEmptyOrZeroLength(key)) {
            queryConstraints.push(where('key', '==', key));
        }

        if (isBooleanTrue(active)) {
            queryConstraints.push(where('active', '==', true));
        } else if (isBooleanFalse(active)) {
            queryConstraints.push(where('active', '==', false));
        }

        if (isListNotEmpty(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', [...coveredEntityId, 'ALL']));
        } else if (isStringNotEmptyOrZeroLength(coveredEntityId)) {
            queryConstraints.push(where('coveredEntityId', 'in', [coveredEntityId, 'ALL']));
        }

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Agreement.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Agreement.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Agreement.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    static replaceInAgreement = (content, data = {}, otherInfo = {}) => {
        return content.replace(
            /{{(\w*)}}/g,
            function (m, key) {
                return (isObjectNotEmpty(data) && data?.hasOwnProperty(key)) ? data[key] : (CONSTANTS.hasOwnProperty(key) ? CONSTANTS[key] : (isObjectNotEmpty(otherInfo) && otherInfo?.hasOwnProperty(key) ? otherInfo[key] : ""));
            }
        ).replace('\t', '').replace('\n', '').replace('\r', '');
    }

    static getAgreement = async (key, contact = {}, otherInfo = {}) => {
        if (isStringNotEmpty(key)) {
            const ipAddress = await getClientIPAddress();
            let agreement = await this.getByKeyAndActive(key);
            if (isObjectNotEmpty(agreement)) {
                let filledAgreement = this.replaceInAgreement(agreement.content, contact, { ...otherInfo, ipAddress: ipAddress, signatureDate: moment().toISOString(), signatureName: isObjectNotEmpty(contact) ? toName(contact) : `User at ${ipAddress}` });

                return { ...agreement, content: filledAgreement, signatureName: isObjectNotEmpty(contact) ? toName(contact) : null, signatureDate: moment().toISOString(), ipAddress: ipAddress };
            }
        }

        return null;
    }

    static getAgreementForGenerator = async (key, ipAddress, signatureName, signatureDate, otherInfo = {}) => {
        if (isStringNotEmpty(key)) {
            let agreement = await this.getByKeyAndActive(key);
            if (isObjectNotEmpty(agreement)) {
                let filledAgreement = this.replaceInAgreement(agreement.content, {}, { ...otherInfo, ipAddress: ipAddress, signatureDate: signatureDate, signatureName: signatureName });

                return { ...agreement, content: filledAgreement, signatureName: signatureName, signatureDate: signatureDate, ipAddress: ipAddress };
            }
        }

        return null;
    }

    static getAgreements = async (keys, contact = {}, otherInfo = {}) => {
        if (isListNotEmpty(keys)) {
            console.log(keys)
            const ipAddress = await getClientIPAddress();
            let agreements = [];
            for (const key of keys) {
                let agreement = await this.getByKeyAndActive(key);
                if (isObjectNotEmpty(agreement)) {
                    let filledAgreement = this.replaceInAgreement(agreement.content, contact, { ...otherInfo, ipAddress: ipAddress, signatureDate: moment().toISOString(), signatureName: isObjectNotEmpty(contact) ? toName(contact) : `User at ${ipAddress}` });

                    agreements.push({ ...agreement, content: filledAgreement, signatureName: isObjectNotEmpty(contact) ? toName(contact) : null, signatureDate: moment().toISOString(), ipAddress: ipAddress });
                }
            }

            return agreements;
        }

        return [];
    }
};

export default Agreement;